import React, { useState, useEffect } from "react";
import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from "@mui/material";
import api from "../api";

/**
 * Group (course plan) picker for the tutor's class forms.
 *
 * Only groups the tutor **leads** can host a class — the server enforces the same rule in
 * ClassroomController::store() via userStaffsPlan(..., [ROLE_TUTOR]), so filtering on
 * `can_host_class` here just avoids offering a choice that would be refused.
 *
 * "Whole course" is offered explicitly rather than by leaving the field blank: a class with no
 * group is visible to every student on the course, and that should be a deliberate choice rather
 * than the accident of not touching a dropdown.
 *
 * @param {string|number} value          selected course_plan_id, or "" for whole-course
 * @param {(id: string) => void} onChange
 * @param {string|number} [courseId]     when set, only that course's groups are listed
 * @param {(group: object|null) => void} [onGroupLoaded] receives the selected group object
 */
export default function GroupSelect({
  value,
  onChange,
  courseId,
  disabled,
  size = "small",
  allowWholeCourse = true,
  helperText,
  onGroupLoaded,
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get("tutor/groups")
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setGroups(list.filter((g) => g.can_host_class && g.is_active));
      })
      .catch(() => {
        // A group-list failure must not block the course-wide path, which still works.
        if (!cancelled) setGroups([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = courseId
    ? groups.filter((g) => String(g.course?.id) === String(courseId))
    : groups;

  // Clear a selection that the current course filter no longer offers.
  useEffect(() => {
    if (value && !visible.some((g) => String(g.id) === String(value))) {
      onChange("");
      onGroupLoaded?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, groups]);

  const hint =
    helperText ??
    (loading
      ? "Loading your groups…"
      : visible.length === 0
        ? "You do not lead an active group on this course."
        : "A group class is seen only by that group's students.");

  return (
    <FormControl size={size} fullWidth disabled={disabled || loading}>
      <InputLabel>Group</InputLabel>
      <Select
        value={value ?? ""}
        label="Group"
        onChange={(e) => {
          onChange(e.target.value);
          onGroupLoaded?.(visible.find((g) => String(g.id) === String(e.target.value)) || null);
        }}
      >
        {allowWholeCourse && <MenuItem value="">Whole course (no group)</MenuItem>}
        {visible.map((g) => (
          <MenuItem key={g.id} value={String(g.id)}>
            {g.course?.name ? `${g.course.name} — ${g.name}` : g.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>{hint}</FormHelperText>
    </FormControl>
  );
}
